// Fase 21C-4A: lógica pura del formulario del catálogo de Productos Otros. Aislada aquí (mismo
// criterio que otherSaleTax.ts / otherSaleActions.ts) para poder probarla como función pura —
// el frontend de este repo no tiene jsdom/testing-library, así que la lógica se prueba aquí y el
// componente solo la orquesta. El backend sigue siendo la validación final.
import type {
  CostMode,
  CreateOtherSaleProductInput,
  OtherSaleProduct,
  OtherSaleTaxMode,
  UpdateOtherSaleProductInput,
} from '../types/otherSales';
import { formatMoney } from './costFormatting';

// Modos fiscales que un formulario administrativo PUEDE enviar. Deliberadamente sin 'MIXED':
// ese valor solo lo genera el servidor en el encabezado de una venta con líneas mezcladas y jamás
// debe ofrecerse como opción de captura.
export const PRODUCT_TAX_MODE_OPTIONS: ReadonlyArray<readonly [OtherSaleTaxMode, string]> = [
  ['NONE', 'Sin IVA'],
  ['INCLUDED', 'IVA incluido (16%)'],
  ['CUSTOM', 'Otro % (especificar)'],
];

// Etiqueta para MOSTRAR el tratamiento fiscal de una venta ya registrada (respuesta). Incluye
// 'MIXED' — resuelve la deuda de 21C-3: una venta del mini-POS con IVA mixto se muestra sin romper.
const RESPONSE_TAX_MODE_LABELS: Record<string, string> = {
  NONE: 'Sin IVA',
  INCLUDED: 'IVA incluido',
  CUSTOM: 'IVA personalizado',
  MIXED: 'IVA mixto',
};

export function formatResponseTaxMode(taxMode: string): string {
  return RESPONSE_TAX_MODE_LABELS[taxMode] ?? taxMode;
}

// P-071: las tres formas de llevar el costo, con sus nombres en el formulario de edición (el asistente
// «Nuevo producto» tiene los suyos en newProduct.ts).
export const PRODUCT_COST_MODE_OPTIONS: ReadonlyArray<readonly [CostMode, string]> = [
  ['NONE', 'Solo vender (sin costo por ahora)'],
  ['APPROXIMATE', 'Costo aproximado por unidad'],
  ['RECIPE', 'Según receta (control completo)'],
];

export interface ProductFormState {
  name: string;
  price: string;
  taxMode: OtherSaleTaxMode;
  taxPercentage: string;
  displayOrder: string;
  /** P-071: cómo se lleva el costo. Decide qué viaja en recipeId y approximateUnitCost. */
  costMode: CostMode;
  recipeId: string; // '' = sin receta (solo cuenta con costMode RECIPE)
  approximateUnitCost: string; // '' = sin costo (solo cuenta con costMode APPROXIMATE)
  storeIds: string[]; // [] = todas las tiendas
}

export function emptyProductForm(): ProductFormState {
  return { name: '', price: '', taxMode: 'NONE', taxPercentage: '', displayOrder: '0', costMode: 'NONE', recipeId: '', approximateUnitCost: '', storeIds: [] };
}

/**
 * Validación mínima de UX (el backend es la fuente final). Devuelve el primer mensaje de error, o
 * null si el formulario es válido.
 */
export function validateProductForm(form: ProductFormState): string | null {
  if (!form.name.trim()) return 'El nombre es obligatorio.';
  const price = Number(form.price);
  if (form.price.trim() === '' || Number.isNaN(price) || !Number.isFinite(price) || price < 0) {
    return 'El precio no puede ser negativo.';
  }
  if (form.displayOrder.trim() !== '' && !Number.isInteger(Number(form.displayOrder))) {
    return 'El orden de visualización debe ser un número entero.';
  }
  if (form.taxMode === 'CUSTOM') {
    const percentage = Number(form.taxPercentage);
    if (form.taxPercentage.trim() === '' || Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
      return 'Especifica el porcentaje de IVA (0 a 100).';
    }
  }
  // P-071: con costo aproximado, un número > 0 (cero no es un costo conocido) con hasta dos decimales (lo que
  // cabe en la columna; un «1.005» que el servidor redondeara no sería lo escrito); con receta, una receta.
  if (form.costMode === 'APPROXIMATE') {
    const cost = Number(form.approximateUnitCost.trim().replace(',', '.'));
    if (form.approximateUnitCost.trim() === '' || !Number.isFinite(cost) || cost <= 0) {
      return 'Escribe el costo aproximado por unidad (mayor que cero).';
    }
    if (Number(cost.toFixed(2)) !== cost) return 'El costo aproximado admite hasta dos decimales.';
  }
  if (form.costMode === 'RECIPE' && !form.recipeId) return 'Elige la receta del producto.';
  return null;
}

export function isProductFormValid(form: ProductFormState): boolean {
  return validateProductForm(form) === null;
}

/**
 * ZTV-07 (pre-client activation hardening): por qué "Guardar" está deshabilitado, en el idioma del
 * usuario (mismo patrón que el verificador del Ticket Mixto y los cortes). Nunca debe pasar que la
 * persona toque Guardar y "no pase nada": si falta la confirmación del cambio de receta, se dice
 * exactamente qué casilla marcar. null = se puede guardar.
 */
export function productSaveDisabledReason(
  form: ProductFormState,
  needsRecipeConfirmation: boolean,
  recipeChangeConfirmed: boolean,
): string | null {
  const validation = validateProductForm(form);
  if (validation) return validation;
  if (needsRecipeConfirmation && !recipeChangeConfirmed) {
    return 'Marca la casilla "Entiendo: este cambio solo afecta ventas futuras" para poder guardar.';
  }
  return null;
}

/**
 * Construye el payload que se manda al backend. NONE/INCLUDED nunca envían un porcentaje (se limpia
 * a null); solo CUSTOM lo incluye. storeIds vacío = disponible en todas las tiendas. P-071: el modo de
 * costo viaja siempre, y solo viaja lo que ese modo usa (la receta con RECIPE, el costo con
 * APPROXIMATE); lo demás va en null para que el servidor nunca vea receta y costo a la vez.
 */
export function buildProductPayload(form: ProductFormState): CreateOtherSaleProductInput {
  return {
    name: form.name.trim(),
    price: Number(form.price),
    taxMode: form.taxMode,
    taxPercentage: form.taxMode === 'CUSTOM' ? Number(form.taxPercentage) : null,
    displayOrder: form.displayOrder.trim() === '' ? 0 : Number(form.displayOrder),
    costMode: form.costMode,
    recipeId: form.costMode === 'RECIPE' ? form.recipeId || null : null,
    approximateUnitCost: form.costMode === 'APPROXIMATE' ? Number(form.approximateUnitCost.trim().replace(',', '.')) : null,
    storeIds: form.storeIds,
  };
}

/**
 * P-071: cómo se dice el costo de un producto en el catálogo: la receta (por su nombre, si se conoce),
 * «Aprox. $X» o «—» cuando no está definido. La cifra solo llega a quien puede ver costos; si el modo es
 * APPROXIMATE y la cifra no vino, se dice «Aproximado» a secas.
 */
export function formatProductCost(product: Pick<OtherSaleProduct, 'costMode' | 'approximateUnitCost' | 'recipeId'>, recipeName: string | null | undefined): string {
  if (product.costMode === 'RECIPE') return `Receta: ${recipeName ?? 'vinculada'}`;
  if (product.costMode === 'APPROXIMATE') return product.approximateUnitCost ? `Aprox. ${formatMoney(product.approximateUnitCost)}` : 'Aproximado';
  return '—';
}

// La edición usa el mismo cuerpo (todos los campos presentes); el backend acepta un patch parcial y
// aquí siempre mandamos el estado completo del formulario, lo cual es válido para PATCH.
export function buildProductUpdatePayload(form: ProductFormState): UpdateOtherSaleProductInput {
  return buildProductPayload(form);
}

/**
 * "Todas las tiendas" cuando no hay asignaciones; si las hay, los nombres de las tiendas que el
 * usuario puede ver. Las tiendas fuera del alcance del usuario (que no están en `stores`) NO se
 * rotulan como "Tienda desconocida": simplemente se omiten. Caso típico (Fase 21C-4A-1): un MANAGER
 * ve un producto co-asignado a su tienda + otra ajena → muestra solo la suya. Si TODAS las
 * asignaciones quedaran fuera de su alcance (defensivo — el backend ya no lista esos productos a un
 * MANAGER), se muestra "Otras tiendas" en vez de una lista vacía.
 */
export function formatStoreAssignments(storeIds: string[], stores: ReadonlyArray<{ id: string; name: string }>): string {
  if (storeIds.length === 0) return 'Todas las tiendas';
  const knownNames = storeIds
    .map((id) => stores.find((store) => store.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  if (knownNames.length === 0) return 'Otras tiendas';
  return knownNames.join(', ');
}
